import {useTrackFeature} from '@aboutyou/tracking';
import {
  COLORS,
  ErrorBoundary,
  Flyout,
  GenericIconName,
  Header as HeaderBase,
  Modal,
  ModalHeader,
  Z_INDEXES,
} from '@aboutyou/ui-components';
import {
  useBasketContainsCampaignOrCouponItems,
  useBasketCount,
  useWishlistBubbleColour,
  useWishlistCount,
} from '@aboutyou/user';
import {styled} from '@linaria/react';
import React, {useMemo, useRef} from 'react';
import {AboutYouLogo} from '../../features/AboutYouLogo/AboutYouLogo';
import {useGetHomepageTakeover} from '../../features/AboutYouLogo/hooks/useGetHomepageTakeover';
import {useIsNewBannerFrameworkActive} from '../../features/PromotionBanner/utils/useIsNewBannerFrameworkActive';
import {BasketHint} from '../Basket/BasketHint/BasketHint';
import {DiscountBanner} from '../Campaigns/DiscountBanner/DiscountBanner.eager';
import {useSelectedDiscountColor} from '../Campaigns/SelectedDiscounts/useSelectedDiscountColor';
import {NotificationCenter} from '../NotificationCenter/NotificationCenter';
import {NotificationCenterDrawerHeader} from '../NotificationCenter/Stream/NotificationCenterDrawerHeader';
import {DetectAppInstallBannerShowSnippet} from './HeaderInstallBanner/DetectAppInstallBannerShowSnippet';
import {HeaderInstallBanner} from './HeaderInstallBanner/HeaderInstallBanner';
import {useIsBannerOpen} from './HeaderInstallBanner/useIsBannerOpen';

export interface FixedHeaderProps {
  onMenuClick: () => void;
  isCheckout: boolean;
}

export const FixedHeader = ({onMenuClick, isCheckout}: FixedHeaderProps) => {
  const {count: wishlistCount} = useWishlistCount();
  // @TODO: if the A/B test succeeds we should have all the logic related to the icons into a separate commit, as it's done for the desk. See https://aboutyou.atlassian.net/browse/AYFEP-3316
  const basketCount = useBasketCount();
  const activateNewBannerFramework = useIsNewBannerFrameworkActive();
  const basketContainsCampaignItems = useBasketContainsCampaignOrCouponItems();
  const selectedDiscountColor = useSelectedDiscountColor();
  const wishlistBubbleColour = useWishlistBubbleColour();
  const homepageTakeover = useGetHomepageTakeover();

  const trackFeature = useTrackFeature();
  const trackIconClick = (label: string) => {
    trackFeature({eventType: 'click', label});
  };

  const notificationBellRef = useRef<HTMLDivElement>(null);

  const basketHint = useMemo(() => {
    return (
      <ErrorBoundary renderContext="basket_hint" ErrorComponent={null}>
        <BasketHint notificationBellRef={notificationBellRef} />
      </ErrorBoundary>
    );
  }, []);

  return (
    <>
      <DetectAppInstallBannerShowSnippet />
      <HeaderInstallBannerWrapper />
      {!isCheckout && !activateNewBannerFramework && (
        <StyledDiscountBanner showOnlySimpleCampaign />
      )}
      <HeaderBase
        wishlistWithBadge={
          wishlistBubbleColour ? {color: wishlistBubbleColour.hex} : undefined
        }
        wishlistCount={wishlistCount}
        basketCount={basketCount}
        basketWithBadge={
          basketContainsCampaignItems && selectedDiscountColor
            ? {color: selectedDiscountColor}
            : undefined
        }
        onMenuClick={onMenuClick}
        isCheckout={isCheckout}
        logoComponent={
          <AboutYouLogo
            disableShowUserName
            disableLogoLink={false}
            disableBasCampaignTakeover={isCheckout}
            homepageTakeover={homepageTakeover.data?.homepageTakeoverSection}
          />
        }
        trackIconClick={trackIconClick}
        NotificationIconComponent={
          <NotificationCenter
            NotificationCenterStreamComponent={StyledModal}
            topBodyContent={
              <NotificationCenterDrawerHeader
                iconName={GenericIconName.ArrowLeft}
              />
            }
            notificationBellRef={notificationBellRef}
            NewsletterSubscriptionComponent={Flyout}
          />
        }
        shopHintComponents={<>{basketHint}</>}
      />
    </>
  );
};

const HeaderInstallBannerWrapper = () => {
  const {close} = useIsBannerOpen();

  return <HeaderInstallBanner onClose={close} />;
};

const StyledModal = styled(Modal)`
  & > div {
    background-color: ${COLORS.Grey4};
  }

  ${ModalHeader} {
    display: none;
  }
`;

const StyledDiscountBanner = styled(DiscountBanner)`
  box-sizing: border-box;
  height: 50px;
  position: relative;
  z-index: ${Z_INDEXES.SIMPLE_BANNER};
`;
